import { base44 } from "@/api/base44Client";

/**
 * Billing Service - Centralized payment & subscription management
 */

const PREMIUM_TIERS = ["premium", "advanced", "professional", "enterprise"];

const PREMIUM_PLAN = {
  id: "premium-single-plan",
  tier: "premium",
  name: "Premium",
  description: "Full access to all paid Marstick tools, premium planning, fleet insights, and advanced navigation features.",
  monthly_price: 19,
  yearly_price: 190,
  features: [
    "Premium navigation tools",
    "Advanced voyage planning",
    "Marine weather intelligence",
    "Fleet, maintenance and document tools",
    "Performance metrics and premium insights",
    "Priority premium content access"
  ],
  included_pages: ["dashboard", "navigation", "weather", "trips", "boats", "maintenance", "metrics", "offline", "boat-document-vault"],
  is_active: true,
  display_order: 1,
};

const FREE_PLAN = {
  id: "free-public-plan",
  tier: "basic",
  name: "Free",
  description: "Public content, learning resources, marina discovery, weather previews, and basic exploration tools.",
  monthly_price: 0,
  yearly_price: 0,
  features: [
    "Public learning content",
    "Public weather and marina pages",
    "Sailing calculators",
    "Guides and destination content"
  ],
  included_pages: ["learning-center", "marinas", "weather", "sailing-calculators"],
  is_active: true,
  display_order: 0,
};

function normalizeTier(tier) {
  return PREMIUM_TIERS.includes(tier) ? "premium" : "basic";
}

function normalizeSubscription(subscription) {
  if (!subscription) return subscription;
  return {
    ...subscription,
    tier: normalizeTier(subscription.tier),
    name: normalizeTier(subscription.tier) === "premium" ? "Premium" : "Free",
  };
}

function adminOverrideSubscription(user) {
  return {
    id: "admin-override",
    user_email: user?.email,
    tier: "premium",
    status: "active",
    is_admin_override: true,
    name: "Admin Override",
  };
}

function getMetadata(subscription) {
  return subscription?.metadata || {};
}

function getGrantedPremiumPages(subscription) {
  const metadata = getMetadata(subscription);
  return Array.isArray(metadata.admin_granted_pages) ? metadata.admin_granted_pages : [];
}

function hasAdminPageGrant(subscription, featureId) {
  const metadata = getMetadata(subscription);
  return metadata.admin_granted_all === true || getGrantedPremiumPages(subscription).includes(featureId);
}

export const BillingService = {
  /**
   * Admin entitlement override: admins are treated as premium at the subscription layer.
   * This keeps premium access centralized and avoids scattered role checks in UI components.
   */
  isAdminUser(user) {
    return user?.role === "admin" || user?.isAdmin === true;
  },

  async getCurrentUserIfAdmin(userOrEmail) {
    if (typeof userOrEmail === "object" && this.isAdminUser(userOrEmail)) {
      return userOrEmail;
    }

    try {
      const user = await base44.auth.me();
      const requestedEmail = typeof userOrEmail === "string" ? userOrEmail : userOrEmail?.email;
      const sameUser = !requestedEmail || user.email === requestedEmail;
      return sameUser && this.isAdminUser(user) ? user : null;
    } catch {
      return null;
    }
  },

  isAdminOverride(subscription) {
    return subscription?.is_admin_override === true;
  },

  isPremiumTier(tier) {
    return PREMIUM_TIERS.includes(tier);
  },

  /**
   * Get user's current subscription
   */
  async getUserSubscription(userOrEmail) {
    try {
      const adminUser = await this.getCurrentUserIfAdmin(userOrEmail);
      if (adminUser) return adminOverrideSubscription(adminUser);

      const userEmail = typeof userOrEmail === "string" ? userOrEmail : userOrEmail?.email;
      if (!userEmail) return null;

      const subs = await base44.entities.UserSubscription.filter({ user_email: userEmail });
      return normalizeSubscription(subs[0]) || null;
    } catch (error) {
      console.error("Failed to fetch subscription", error);
      return null;
    }
  },

  /**
   * Get all subscription plans
   */
  async getPlans() {
    try {
      const plans = await base44.entities.SubscriptionPlan.list("-display_order");
      const premiumFromData = plans.find((plan) => PREMIUM_TIERS.includes(plan.tier));
      return [
        { ...FREE_PLAN, ...(plans.find((plan) => plan.tier === "basic") || {}) },
        { ...PREMIUM_PLAN, ...(premiumFromData || {}), tier: "premium", name: "Premium" }
      ];
    } catch (error) {
      console.error("Failed to fetch plans", error);
      return [];
    }
  },

  /**
   * Get plan by tier
   */
  async getPlanByTier(tier) {
    try {
      const normalizedTier = normalizeTier(tier);
      if (normalizedTier === "basic") return FREE_PLAN;
      const plans = await this.getPlans();
      return plans.find((plan) => plan.tier === "premium") || PREMIUM_PLAN;
    } catch (error) {
      console.error("Failed to fetch plan", error);
      return null;
    }
  },

  /**
   * Check if user has access to feature
   */
  async hasFeatureAccess(userEmail, featureId) {
    try {
      const subscription = await this.getUserSubscription(userEmail);
      if (!subscription) return false;
      if (this.isAdminOverride(subscription) || hasAdminPageGrant(subscription, featureId)) return true;

      const features = await base44.entities.FeatureAccess.filter({ feature_id: featureId });
      const feature = features[0];
      if (!feature) return false;

      const tiers = feature.available_tiers || [];
      return tiers.includes(subscription.tier) || (subscription.tier === "premium" && tiers.some((tier) => PREMIUM_TIERS.includes(tier)));
    } catch (error) {
      console.error("Failed to check feature access", error);
      return false;
    }
  },

  /**
   * Check broad premium entitlement without tying access to one feature rule.
   */
  async hasPremiumAccess(userEmail) {
    const subscription = await this.getUserSubscription(userEmail);
    return this.isAdminOverride(subscription) || getMetadata(subscription).admin_granted_all === true || normalizeTier(subscription?.tier) === "premium";
  },

  async getPremiumEntitlements(userEmail) {
    const subscription = await this.getUserSubscription(userEmail);
    const all = this.isAdminOverride(subscription) || getMetadata(subscription).admin_granted_all === true || normalizeTier(subscription?.tier) === "premium";
    return { all, pages: all ? [] : getGrantedPremiumPages(subscription) };
  },

  /**
   * Get all accessible features for a tier
   */
  async getFeaturesForTier(tier) {
    try {
      const features = await base44.entities.FeatureAccess.list();
      const normalizedTier = normalizeTier(tier);
      return features.filter((feature) => {
        const tiers = feature.available_tiers || [];
        return normalizedTier === "premium"
          ? tiers.some((item) => PREMIUM_TIERS.includes(item))
          : tiers.includes("basic");
      });
    } catch (error) {
      console.error("Failed to fetch features", error);
      return [];
    }
  },

  /**
   * Get payment provider configuration
   */
  async getPaymentProvider(providerType) {
    try {
      const providers = await base44.entities.PaymentProvider.filter({
        provider_type: providerType,
        is_enabled: true
      });
      return providers[0] || null;
    } catch (error) {
      console.error("Failed to fetch payment provider", error);
      return null;
    }
  },

  /**
   * Get all enabled payment providers
   */
  async getEnabledProviders() {
    try {
      const providers = await base44.entities.PaymentProvider.filter({ is_enabled: true });
      return providers;
    } catch (error) {
      console.error("Failed to fetch providers", error);
      return [];
    }
  },

  /**
   * Get user's transaction history
   */
  async getUserTransactions(userEmail, limit = 20) {
    try {
      const transactions = await base44.entities.Transaction.filter(
        { user_email: userEmail },
        "-created_date",
        limit
      );
      return transactions;
    } catch (error) {
      console.error("Failed to fetch transactions", error);
      return [];
    }
  },

  /**
   * Create transaction record
   */
  async createTransaction(data) {
    try {
      return await base44.entities.Transaction.create(data);
    } catch (error) {
      console.error("Failed to create transaction", error);
      throw error;
    }
  },

  /**
   * Update subscription
   */
  async updateSubscription(userEmail, updateData) {
    try {
      const subs = await base44.entities.UserSubscription.filter({ user_email: userEmail });
      if (subs[0]) {
        return await base44.entities.UserSubscription.update(subs[0].id, updateData);
      }
    } catch (error) {
      console.error("Failed to update subscription", error);
      throw error;
    }
  },

  /**
   * Get upgrade suggestion (next higher tier)
   */
  async getUpgradeSuggestion(currentTier) {
    return normalizeTier(currentTier) === "basic" ? await this.getPlanByTier("premium") : null;
  },

  /**
   * Calculate plan comparison data
   */
  async getPlanComparison() {
    try {
      const plans = await this.getPlans();
      const features = await base44.entities.FeatureAccess.list();

      return {
        plans,
        features,
        comparisonMatrix: plans.map(plan => ({
          ...plan,
          included_features: features.filter(f => f.available_tiers?.includes(plan.tier))
        }))
      };
    } catch (error) {
      console.error("Failed to get plan comparison", error);
      return { plans: [], features: [], comparisonMatrix: [] };
    }
  }
};

export default BillingService;